<template>
    <Statusbar v-if="data" :status="data.status" :next-step="data.next_step" />
    <div v-if="data" class="ml-md mt-md col-span-full overflow-auto">
        <div class="gap-md flex flex-col">
            <h2>Summary</h2>
            <!-- Below md the summary blocks stack full-width; md keeps the original four-column layout. -->
            <div class="gap-y-md grid grid-cols-1 md:grid-cols-4">
                <h3 class="col-span-full text-[16px]">New organization info</h3>
                <div class="gap-sm col-span-full md:col-span-2 flex flex-col">
                    <h3>Organization info:</h3>
                    <OrgInfo :company="data.organization.name" :level="data.organization.service_level_id" />
                </div>

                <div class="gap-sm col-span-full md:col-span-2 flex flex-col">
                    <h3>Primary contact:</h3>
                    <div class="gap-sm flex">
                        <div class="gap-xs flex items-center">
                            <i class="i-phone" />
                            <p>{{ data.contact_phone }}</p>
                        </div>
                        <div class="gap-xs flex items-center">
                            <i class="i-postcard" />
                            <p>{{ data.contact_email }}</p>
                        </div>
                    </div>
                    <OrgInfo :company="`${data.contact_first_name} ${data.contact_last_name}`" />
                </div>

                <div v-if="numberOfSelectedLocations > 0" class="gap-sm col-span-full md:col-span-3 flex flex-col">
                    <DropdownContent default-open>
                        <template #trigger>
                            <div class="gap-sm flex capitalize">
                                <h2>Selected Locations</h2>
                                <TagComponent :value="numberOfSelectedLocations?.toString()" />
                            </div>
                        </template>

                        <template #content>
                            <CascadingLocationsUuid :items="locations! as UUIDLocation[]" :expand-all="true" />
                        </template>
                    </DropdownContent>
                </div>

                <div v-if="serviceDesks?.length" class="gap-sm col-span-full md:col-span-3 flex flex-col">
                    <DropdownContent>
                        <template #trigger>
                            <div class="gap-sm flex capitalize">
                                <h2>Service Desks</h2>
                                <TagComponent :value="serviceDesks?.length?.toString()" />
                            </div>
                        </template>

                        <template #content>
                            <ServiceDeskDropdown :read-only-desks="serviceDesks" :editable="false" />
                        </template>
                    </DropdownContent>
                </div>

                <div v-if="roles?.length" class="gap-sm col-span-full md:col-span-3 flex flex-col">
                    <DropdownContent>
                        <template #trigger>
                            <div class="gap-sm flex capitalize">
                                <h2>Selected roles</h2>
                                <TagComponent :value="roles?.length?.toString()" />
                            </div>
                        </template>

                        <template #content>
                            <div class="gap-xs flex flex-col">
                                <RoleRow
                                    v-for="role in roles"
                                    :key="role.organization_role_id"
                                    :level="mapRoleLevelToName(role.service_level_id) ?? null"
                                    :role="role.name"
                                    no-drag
                                />
                            </div>
                        </template>
                    </DropdownContent>
                </div>

                <div v-if="groups?.selected?.length" class="gap-sm col-span-full md:col-span-3 flex flex-col">
                    <DropdownContent>
                        <template #trigger>
                            <div class="gap-sm flex capitalize">
                                <h2>Selected groups</h2>
                                <TagComponent :value="groups?.selected?.length?.toString()" />
                            </div>
                        </template>

                        <template #content>
                            <div class="gap-xs flex flex-col">
                                <GroupCard
                                    v-for="group in groups.selected"
                                    :key="group.default_group_id"
                                    :name="group.name"
                                    :roles="group.organization_roles"
                                    :sites="group.sites"
                                    :regions="group.regions"
                                    :request-types="group.request_types"
                                    :drag-enabled="false"
                                />
                            </div>
                        </template>
                    </DropdownContent>
                </div>

                <div class="gap-sm col-span-full md:col-span-3 flex flex-col">
                    <DropdownContent>
                        <template #trigger>
                            <div class="gap-sm flex">
                                <h2>SLA lines</h2>
                            </div>
                        </template>

                        <template #content>
                            <SlaLineComponent :sla-lines="SLALines ?? []" readonly />
                        </template>
                    </DropdownContent>
                </div>
            </div>
        </div>
    </div>

    <BottomButtons
        :handle-submit="submit"
        arrow
        :complete-text="nextStep === 'Summary' ? 'Review' : 'Create organization'"
        :disabled="disableButtons"
        :sticky="false"
        :loading="loading"
        dont-disable
        @cancel="$emit('cancel')"
    />
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import Statusbar from '@/components/admin/organization/creation-process/status-bar.vue';
import OrgInfo from './org-info.vue';
import SlaLineComponent from '@/components/admin/sla-lines.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';

import type {
    AdminOrganizationDefaultGroup,
    AdminOrganizationFormData,
    Role,
    UUIDLocation,
    AdminServiceDeskReadOnly,
    AdminContract,
} from '@/stores/store-interfaces';
import RoleRow from './role-row.vue';
import { mapRoleLevelToName } from '@/stores/helpers';
import BottomButtons from '@/components/bottom-buttons.vue';
import GroupCard from './group-card.vue';
import CascadingLocationsUuid from '@/components/misc/cascading-locations-uuid.vue';
import DropdownContent from '@/components/misc/dropdown-content.vue';
import ServiceDeskDropdown from '@/components/misc/service-desk-dropdown.vue';
import { useAdminStore } from '@/stores/admin';

const props = defineProps({
    data: Object as PropType<AdminOrganizationFormData>,
    roles: Array as PropType<Role[]>,
    groups: Object as PropType<{ selected: AdminOrganizationDefaultGroup[] }>,
    locations: Array as PropType<UUIDLocation[]>,
    numberOfSelectedLocations: { type: Number, required: true },
    serviceDesks: Array as PropType<AdminServiceDeskReadOnly[]>,
    slaLines: { type: Object as PropType<AdminContract['sla']> },
    nextStep: String,
    disable: Boolean,
    loading: Boolean,
});

const disableButtons = computed(
    () =>
        (props.data!.status === 'WAITING FOR APPROVAL'
            ? !(useAdminStore().userCPRoles.includes('noc-admin') || useAdminStore().userCPRoles.includes('admin'))
            : !(useAdminStore().userCPRoles.includes('cdim-admin') || useAdminStore().userCPRoles.includes('admin'))) ||
        props.data!.status === 'APPROVED' ||
        props.disable,
);

const emit = defineEmits(['cancel', 'submit']);
const submit = () => {
    emit('submit', props.nextStep); // This is maybe a little redundant
};

const SLALines = computed(
    () =>
        props.slaLines?.map((line) => {
            const unit = line.sla_type.unit as 'C' | 'Hours' | '%' | 'Days';
            return line.sla_type.name === 'Notification'
                ? {
                      ...line,
                      sla_type: {
                          ...line.sla_type,
                          unit: 'Days' as 'C' | 'Hours' | '%' | 'Days',
                      },
                  }
                : { ...line, sla_type: { ...line.sla_type, unit } };
        }) ?? [],
);
</script>
